import type { MediaSource_, PlaybackPlan, Chapter } from '../model/playback.ts';
import type { VideoDetails } from '../model/video.ts';
import { parseChapters } from '../parse/chapters.ts';

export interface PlaybackPlanOptions {
  /** ISO-3166, from `Settings.contentRegion`. Omit to skip the region gate entirely. */
  readonly contentRegion?: string | null;
  /**
   * `shaka.Player.isBrowserSupported()` on web. Only affects the DASH path: iOS < 17.1 has no MSE
   * but does play HLS natively from a plain `src`, so a live/DVR plan stays valid without it.
   */
  readonly mseSupported?: boolean;
  /**
   * Overrides the chapters the plan would otherwise parse out of the description. Supply this
   * only when they come from somewhere else (a test, or an endpoint that grows a `chapters` field
   * — this instance has none today). Omitting it is the normal path.
   */
  readonly chapters?: readonly Chapter[];
}

function mediaSource(video: VideoDetails, manifestUrl: string, manifestKind: 'dash' | 'hls'): MediaSource_ {
  return {
    manifestUrl,
    manifestKind,
    fallbackProgressiveUrl: video.progressiveFallbackUrl,
    subtitles: video.subtitles,
    storyboardVttUrl: video.storyboard === null ? null : video.storyboard.vttUrl,
    expiresAtEpochSec: video.mediaExpiresAtEpochSec,
  };
}

/**
 * R5 — the whole point of the union: `isUpcoming`, `isPostLiveDvr`, `paid`/`premium`,
 * `allowedRegions` and the no-MSE case are all real states the Watch page has to render, and none
 * of them is "an error".
 *
 * Branching is on the **booleans**, never on `type`: the API reports `type: "scheduled"` for
 * ordinary finished VODs (verified on `SqcY0GlETPk`).
 *
 * The live branch is the one with teeth. §2 trap 2: `dashUrl` is populated on live videos but
 * serves an empty `<Period/>` (later, a 500), so `liveNow || isPostLiveDvr` must go to `hlsUrl` —
 * and §4 records one captured `isPostLiveDvr` video with **no `hlsUrl` at all**. That is why the
 * fallback ends in `unplayable: 'no-source'` rather than in `undefined`.
 */
export function toPlaybackPlan(video: VideoDetails, options: PlaybackPlanOptions = {}): PlaybackPlan {
  if (video.isUpcoming) {
    return { kind: 'upcoming', premiereAtEpochSec: video.premiereAtEpochSec };
  }

  const region = options.contentRegion;
  if (region !== undefined && region !== null && region !== '' && video.allowedRegions.length > 0) {
    if (!video.allowedRegions.includes(region.toUpperCase())) {
      return { kind: 'unplayable', reason: 'region' };
    }
  }

  if (video.paid || video.premium) {
    return { kind: 'unplayable', reason: 'paid' };
  }

  if (video.liveNow || video.isPostLiveDvr) {
    if (video.hlsUrl === null) return { kind: 'unplayable', reason: 'no-source' };
    const source = mediaSource(video, video.hlsUrl, 'hls');
    if (video.liveNow) return { kind: 'live', source };
    return { kind: 'dvr', source, durationSec: video.lengthSeconds };
  }

  if (video.dashUrl === null) {
    return { kind: 'unplayable', reason: 'no-source' };
  }

  if (options.mseSupported === false && video.progressiveFallbackUrl === null) {
    return { kind: 'unplayable', reason: 'no-mse' };
  }

  return {
    kind: 'vod',
    source: mediaSource(video, video.dashUrl, 'dash'),
    // Derived here rather than left to the caller: a Watch page that forgets to call
    // `parseChapters` shows a plain scrub bar and nothing anywhere reports the omission.
    chapters: options.chapters ?? parseChapters(video.description, video.lengthSeconds),
    durationSec: video.lengthSeconds,
  };
}
